import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

export interface Subscription {
  id: string;
  user_id: string;
  plan_tier: 'free' | 'basic' | 'premium';
  status: 'active' | 'past_due' | 'canceled' | 'grace_period' | 'pending' | 'pending_downgrade';
  pagarme_subscription_id: string | null;
  active_until: string;
  billing_cycle: 'monthly' | 'semester' | 'annual';
  amount_cents: number;
  created_at: string;
  updated_at: string;
  pending_downgrade_to?: 'free' | 'basic' | 'premium' | null;
  pending_downgrade_at?: string | null;
}

export interface FeatureUsage {
  feature_key: string;
  used_count: number;
  limit_value: number;
  period_start: string;
  period_end: string;
}

export interface SubscriptionLimits {
  max_family_members: number;
  max_sos_consultations_per_month: number;
  max_prescription_analysis_days: number;
  analysis_ia_enabled: boolean;
}

interface SubscriptionContextValue {
  subscription: Subscription | null;
  limits: SubscriptionLimits | null;
  usage: FeatureUsage[];
  loading: boolean;
  error: string | null;
  canUseFeature: (featureKey: string) => boolean;
  getFeatureUsage: (featureKey: string) => { used: number; limit: number };
  isInGracePeriod: () => boolean;
  isActive: () => boolean;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [limits, setLimits] = useState<SubscriptionLimits | null>(null);
  const [usage, setUsage] = useState<FeatureUsage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const debounceTimerRef = useRef<number | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  const fetchCallsRef = useRef<number>(0);

  const isInGracePeriod = useMemo(() => {
    return () => subscription?.status === 'grace_period';
  }, [subscription?.status]);

  const isActive = useMemo(() => {
    return () => {
      if (!subscription) return false;
      const now = new Date();
      const activeUntil = new Date(subscription.active_until);

      if (subscription.status === 'active' || subscription.status === 'grace_period') {
        return true;
      }
      if (subscription.status === 'canceled' && now < activeUntil) {
        return true;
      }
      if (subscription.status === 'pending_downgrade' && now < activeUntil) {
        return true;
      }
      return false;
    };
  }, [subscription]);

  const getFeatureUsage = (featureKey: string) => {
    const fu = usage.find((u) => u.feature_key === featureKey);
    if (!fu) return { used: 0, limit: Infinity };
    return { used: fu.used_count, limit: fu.limit_value };
  };

  const canUseFeature = (featureKey: string) => {
    if (!subscription || !limits) return false;
    if (!isActive()) return false;
    const fu = usage.find((u) => u.feature_key === featureKey);
    if (!fu) return true;
    return fu.used_count < fu.limit_value;
  };

  async function fetchSubscription() {
    if (!user) {
      setSubscription(null);
      setLimits(null);
      setUsage([]);
      setLoading(false);
      return;
    }

    try {
      if (isFetchingRef.current) {
        if (import.meta.env.DEV) {
          console.debug('[SubscriptionProvider] fetchSubscription() ignorado: já em andamento');
        }
        return;
      }
      isFetchingRef.current = true;
      fetchCallsRef.current += 1;
      if (import.meta.env.DEV) {
        console.debug('[SubscriptionProvider] fetchSubscription() iniciado', { calls: fetchCallsRef.current });
      }
      setLoading(true);
      setError(null);

      // Assinatura do usuário
      const { data: subData, error: subError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (subError && (subError as any).code !== 'PGRST116') {
        throw subError;
      }

      setSubscription(subData as any);

      // Limites do plano
      if (subData) {
        const { data: planData, error: planError } = await supabase
          .from('subscription_plans_public')
          .select('max_family_members, max_sos_consultations_per_month, max_prescription_analysis_days, analysis_ia_enabled')
          .eq('tier', (subData as any).plan_tier)
          .single();
        if (planError) throw planError;

        setLimits({
          max_family_members: (planData as any).max_family_members,
          max_sos_consultations_per_month: (planData as any).max_sos_consultations_per_month,
          max_prescription_analysis_days: (planData as any).max_prescription_analysis_days,
          analysis_ia_enabled: (planData as any).analysis_ia_enabled,
        });

        // Uso de features
        const { data: usageData, error: usageError } = await supabase
          .from('feature_usage')
          .select('*')
          .eq('user_id', user.id);
        if (usageError) throw usageError;
        setUsage(usageData || []);
      } else {
        setLimits(null);
        setUsage([]);
      }
    } catch (err: any) {
      console.error('[SubscriptionProvider] Erro ao buscar assinatura:', err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
      if (import.meta.env.DEV) {
        console.debug('[SubscriptionProvider] fetchSubscription() concluído');
      }
      isFetchingRef.current = false;
    }
  }

  // Debounce para coalescer múltiplos eventos em ~400ms
  const debouncedRefresh = () => {
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(() => {
      fetchSubscription();
    }, 400);
  };

  // Carregar no mount e quando user mudar
  useEffect(() => {
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Realtime - único canal por usuário
  useEffect(() => {
    if (!user) return;

    const channelName = `subscription-changes-${user.id}`;
    if (import.meta.env.DEV) {
      console.debug('[SubscriptionProvider] Subscribing channel', { channelName });
    }
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_subscriptions', filter: `user_id=eq.${user.id}` },
        (payload: any) => {
          if (import.meta.env.DEV) {
            try {
              console.debug('[SubscriptionProvider] Subscription changed:', {
                eventType: payload?.eventType,
                new: payload?.new,
                old: payload?.old,
              });
            } catch {
              // Silencioso - apenas debug
            }
          }
          debouncedRefresh();
        },
      )
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
        if (import.meta.env.DEV) {
          console.debug('[SubscriptionProvider] Unsubscribed channel', { channelName });
        }
      } catch {
        // Silencioso - cleanup
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const value: SubscriptionContextValue = {
    subscription,
    limits,
    usage,
    loading,
    error,
    canUseFeature,
    getFeatureUsage,
    isInGracePeriod,
    isActive,
    refresh: fetchSubscription,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSubscriptionContext(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscriptionContext deve ser usado dentro de <SubscriptionProvider>');
  }
  return ctx;
}
